import { create } from 'zustand';
import { supabase } from '../lib/supabase';

interface User {
  id: string;
  email: string;
  display_name: string;
  avatar_emoji: string;
  is_pro: boolean;
  pro_trial_ends_at?: string;
}

interface AuthState {
  user: User | null;
  isLoading: boolean;
  isPro: boolean;
  setUser: (user: User | null) => void;
  login: (email: string, password: string) => Promise<void>;
  signup: (email: string, password: string, display_name: string) => Promise<'signed_in' | 'confirmation_required'>;
  requestPasswordReset: (email: string) => Promise<void>;
  resendConfirmation: (email: string) => Promise<void>;
  restoreRecoverySession: () => Promise<boolean>;
  updatePassword: (password: string) => Promise<void>;
  logout: () => Promise<void>;
  checkSession: () => Promise<void>;
}

type AuthCallbackFlow = 'signup' | 'recovery';

const getAuthCallbackUrl = (flow: AuthCallbackFlow) =>
  typeof window !== 'undefined'
    ? `${window.location.origin}/auth/callback?flow=${flow}`
    : undefined;

const RECOVERY_SESSION_KEY = 'pairtab.recovery.session';

const getStoredRecoverySession = () => {
  if (typeof window === 'undefined') return null;

  const raw = window.sessionStorage.getItem(RECOVERY_SESSION_KEY);
  if (!raw) return null;

  try {
    return JSON.parse(raw) as { access_token: string; refresh_token: string };
  } catch {
    window.sessionStorage.removeItem(RECOVERY_SESSION_KEY);
    return null;
  }
};

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  isLoading: true,
  isPro: false,

  setUser: (user) => {
    set({ user });
    if (user) {
      const isPro = user.is_pro || Boolean(
        user.pro_trial_ends_at && new Date(user.pro_trial_ends_at) > new Date()
      );
      set({ isPro });
    } else {
      set({ isPro: false });
    }
  },

  login: async (email, password) => {
    const response = await fetch('/api/auth/login', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ email, password })
    });

    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload.error || 'Invalid email or password');
    }

    const { data, error } = await supabase.auth.setSession({
      access_token: payload.session?.access_token,
      refresh_token: payload.session?.refresh_token
    });

    if (error) throw error;
    const user = data.user || payload.user;
    if (!user) {
      throw new Error('Invalid email or password');
    }

    const isPro = user.user_metadata?.is_pro || false;
    set({ 
      user: user as unknown as User,
      isPro
    });
  },

  signup: async (email, password, display_name) => {
    const response = await fetch('/api/auth/signup', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        email,
        password,
        display_name,
        avatar_emoji: '👤'
      })
    });

    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload.error || 'Unable to create account. Please try again.');
    }

    if (!payload.session?.access_token || payload.confirmation_required) {
      return 'confirmation_required';
    }

    const { data, error } = await supabase.auth.setSession({
      access_token: payload.session.access_token,
      refresh_token: payload.session.refresh_token
    });

    if (error) throw error;

    const user = data.user || payload.user;
    if (!user) {
      throw new Error('Unable to create account. Please try again.');
    }

    const isPro = user.user_metadata?.is_pro || false;
    set({
      user: user as unknown as User,
      isPro
    });

    return 'signed_in';
  },

  requestPasswordReset: async (email) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: getAuthCallbackUrl('recovery'),
    });

    if (error) throw error;
  },

  resendConfirmation: async (email) => {
    const { error } = await supabase.auth.resend({
      type: 'signup',
      email,
      options: {
        emailRedirectTo: getAuthCallbackUrl('signup'),
      }
    });

    if (error) throw error;
  },

  restoreRecoverySession: async () => {
    const existingSession = await supabase.auth.getSession();
    if (existingSession.data.session) {
      return true;
    }

    const recoverySession = getStoredRecoverySession();
    if (!recoverySession?.access_token || !recoverySession?.refresh_token) {
      return false;
    }

    const { error } = await supabase.auth.setSession(recoverySession);
    if (error) throw error;

    return true;
  },

  updatePassword: async (password) => {
    const { data, error } = await supabase.auth.updateUser({ password });
    if (error) throw error;

    if (typeof window !== 'undefined') {
      window.sessionStorage.removeItem(RECOVERY_SESSION_KEY);
    }

    const user = data.user;
    if (user) {
      const isPro = user.user_metadata?.is_pro || false;
      set({
        user: user as unknown as User,
        isPro
      });
    }
  },

  logout: async () => {
    await supabase.auth.signOut();
    set({ user: null, isPro: false });
  },

  checkSession: async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        const isPro = session.user.user_metadata?.is_pro || false;
        set({ 
          user: session.user as unknown as User,
          isPro
        });
      }
    } catch (err) {
      console.log('No active session, showing login');
    } finally {
      set({ isLoading: false });
    }
  }
}));
